import { getTimezoneOffset } from 'date-fns-tz';
import type { ChartDataRange, ChartDataType } from '@/features/_shared/chart/types/chartData';
import type { ChartType } from '@/features/_shared/chart/types/highcharts';
import { http } from '@/services/http';
import { queryApiById } from '@/services/http/queryApi';
import { downloadJsonFileFromDatalake } from '@/shared/lib/download/downloadJsonFileFromDatalake';
import type { TemplateChartSeries } from '@/shared/types/charts';

const getOverrideTime = (timezone: string): string => {
  const offsetHours = getTimezoneOffset(timezone) / (1000 * 60 * 60);
  const hours = 12 + Math.round(offsetHours);
  return `${String(hours).padStart(2, '0')}:00`;
};

export type CustomChartDataParams = {
  fileName?: string;
  groupName?: string;
  queryFileId?: string;
  from?: string;
  to?: string;
};

export const chartsApi = {
  getChartData: async (
    templateChartSeries: TemplateChartSeries[],
    chartType: ChartType,
    timezone: string
  ): Promise<ChartDataType> => {
    const overrideTime = getOverrideTime(timezone);
    const symbols = templateChartSeries.map(({ symbol }) => {
      const clonedSymbol = structuredClone(symbol);
      clonedSymbol.overrideTime = overrideTime;
      return clonedSymbol;
    });
    const { data } = await http.data.post(
      `/go/index/graph/${chartType === 'forwardSimple' ? 'curve' : chartType}`,
      {
        symbols
      }
    );
    return data;
  },

  getSeasonalChartData: async (
    templateChartSeries: TemplateChartSeries,
    chartDataRange: ChartDataRange
  ): Promise<ChartDataType> => {
    const symbol = structuredClone(templateChartSeries.symbol);
    symbol.from = chartDataRange.from;
    symbol.to = chartDataRange.to;
    const { data } = await http.data.post(`/go/index/graph/spot/seasonal`, symbol);
    return data;
  },

  getCustomChartConfig: async (fileName?: string, groupName?: string) => {
    if (!fileName || !groupName) return null;
    return downloadJsonFileFromDatalake(fileName, groupName);
  },

  getCustomChartData: async ({
    fileName,
    groupName,
    queryFileId,
    from,
    to
  }: CustomChartDataParams) => {
    if (queryFileId) {
      return queryApiById(queryFileId, from, to);
    }
    if (fileName && groupName) {
      return downloadJsonFileFromDatalake(fileName, groupName);
    }
    return null;
  }
};
